import { useState, useRef, ReactNode } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export interface AlertAction {
	label: string;
	onClick: () => void;
	primary?: boolean;
	dismissOnClick?: boolean;
}

export interface AlertProps {
	type: 'success' | 'warning' | 'info';
	icon: ReactNode;
	title: string;
	message: string;
	actions?: AlertAction[];
	onDismiss: () => void;
}

const Alert = ({ type, icon, title, message, actions, onDismiss }: AlertProps) => {
	const [isDismissing, setIsDismissing] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const bgClasses = {
		success:
			'bg-gradient-to-r from-green-50 to-green-100 border-green-500 dark:from-green-900/20 dark:to-green-800/20',
		warning:
			'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-500 dark:from-amber-900/20 dark:to-amber-800/20',
		info: 'bg-gradient-to-r from-purple-50 to-purple-100 border-purple-500 dark:from-purple-900/20 dark:to-purple-800/20',
	};

	const textClasses = {
		success: 'text-green-800 dark:text-green-300',
		warning: 'text-amber-800 dark:text-amber-300',
		info: 'text-purple-800 dark:text-purple-300',
	};

	const handleDismiss = () => {
		if (isDismissing) return;
		setIsDismissing(true);

		// Animate height collapse
		if (containerRef.current) {
			const element = containerRef.current;
			element.style.height = `${element.offsetHeight}px`;
			element.style.marginBottom = '20px';
			// Force reflow
			element.offsetHeight;
			element.style.height = '0px';
			element.style.marginBottom = '0px';
			element.style.padding = '0px';
			element.style.overflow = 'hidden';
		}

		// Wait for animation to complete before calling onDismiss
		setTimeout(() => {
			onDismiss();
		}, 300);
	};

	const handleActionClick = (action: AlertAction) => {
		action.onClick();
		if (action.dismissOnClick) {
			handleDismiss();
		}
	};

	return (
		<div
			ref={containerRef}
			className={`p-5 rounded-xl mb-5 flex items-start gap-4 border-2 transition-all duration-300 ${isDismissing ? 'animate-slideOut' : 'animate-slideIn'} ${bgClasses[type]}`}
		>
			<div className={`text-3xl flex-shrink-0 ${textClasses[type]}`}>{icon}</div>
			<div className="flex-1">
				<div className={`font-bold text-lg mb-1 ${textClasses[type]}`}>{title}</div>
				<div className={`leading-relaxed ${textClasses[type]} opacity-90`}>{message}</div>
				{actions && actions.length > 0 && (
					<div className="mt-4 flex gap-3">
						{actions.map((action, index) => (
							<button
								key={index}
								onClick={() => handleActionClick(action)}
								className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all cursor-pointer ${
									action.primary
										? 'bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white hover:opacity-90'
										: 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
								}`}
							>
								{action.label}
							</button>
						))}
					</div>
				)}
			</div>
			<button
				onClick={handleDismiss}
				className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer"
			>
				<XMarkIcon className="w-5 h-5" />
			</button>
		</div>
	);
};

export default Alert;
